import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../../components/SEO';
import BreadcrumbSchema from '../../components/BreadcrumbSchema';
import Section from '../../components/v2/Section';
import { CTABlock } from '../../components/v2/CTAButton';
import { CASE_STUDIES } from '../../data/caseStudies';

const LIST_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Audio & Co. client case studies',
  url: 'https://audioandco.com/case-studies',
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: CASE_STUDIES.map((c, i) => ({ '@type': 'ListItem', position: i + 1, url: `https://audioandco.com/case-studies/${c.slug}`, name: c.cardTitle })),
  },
};

const Card = ({ c }) => (
  <Link to={`/case-studies/${c.slug}`} className="group bg-white border border-[#E2E8F0] rounded-[14px] overflow-hidden flex flex-col shadow-[0_6px_18px_rgba(30,42,68,0.06)] transition-[box-shadow,transform] duration-300 hover:shadow-[0_24px_54px_rgba(30,42,68,0.14)] hover:-translate-y-1" data-testid={`case-card-${c.slug}`}>
    <div className="aspect-[16/10] overflow-hidden bg-[#F6F8FB]">
      <img src={`/portfolio/${c.build.folder}/home.jpg`} alt={c.build.shots[0].alt} title={c.build.shots[0].title} width="1440" height="900" loading="lazy" decoding="async" className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]" />
    </div>
    <div className="p-6 sm:p-8 flex-1 flex flex-col">
      <p className="text-xs uppercase tracking-widest text-[#BC516F] font-semibold">Podcast and website for {c.audience}</p>
      <h2 className="font-display font-bold text-[#1E2A44] text-2xl mt-2">{c.cardTitle}</h2>
      <p className="text-[#64748B] text-sm mt-1">{c.client}</p>
      <p className="text-[#475569] mt-4 leading-relaxed flex-1">{c.cardSummary}</p>
      <span className="mt-5 inline-flex items-center gap-1.5 text-[#BC516F] group-hover:text-[#A7468A] font-semibold">Read the case study <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></span>
    </div>
  </Link>
);

export default function CaseStudiesIndex() {
  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]" data-testid="case-studies-index">
      <SEO
        title="Client Case Studies | Podcast & Website Builds | Audio & Co."
        description="Real Audio & Co. client builds: podcasts, business websites, YouTube, articles, books and audiobooks for coaches and authors, all in one place."
        keywords={['podcast case studies', 'podcast and website for coaches', 'podcast and website for authors', 'audio and co case studies']}
      />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }, { name: 'Case studies', path: '/case-studies' }]} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LIST_SCHEMA) }} />
      <Section bg="white" testId="case-studies-hero">
        <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Case studies</p>
        <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mt-3 max-w-4xl">
          Whole businesses, built and run in one place.
        </h1>
        <p className="text-lg text-[#475569] mt-6 max-w-3xl leading-relaxed">
          Each case study shows a full Audio &amp; Co. build: the website, podcast, YouTube, articles, chatbot, book and audiobook,
          with screenshots of the live sites. Want something similar? <Link to="/" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">Discover how Audio &amp; Co. can help</Link>. Shorter client stories and reviews are on the <Link to="/results" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">results page</Link>.
        </p>
      </Section>
      <Section bg="bg" testId="case-studies-list">
        <div className="grid md:grid-cols-2 gap-6">
          {CASE_STUDIES.map((c) => <Card key={c.slug} c={c} />)}
        </div>
      </Section>
      <Section bg="navy" innerClassName="!py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">Could your business be the next case study?</h2>
          <p className="text-white/75 mt-4 text-lg">30-minute call. No pitch, no pressure.</p>
          <div className="mt-7 flex justify-center"><CTABlock size="lg" align="center" invert testIdPrefix="cases-cta" /></div>
        </div>
      </Section>
    </main>
  );
}
